import React, {useMemo, useState} from 'react';
import {Text} from '@visx/text';
import {CircledUserIcon} from '@components/icon/CircledUserIcon';
import getFullName from 'utils/getFullName';
import './UserAvatar.sass';
import {UserId} from 'records/User';

/**
 * Renders user's avatar saved on assets
 */
type UserWithId = {id: UserId; hasAvatar?: boolean} | {userId: UserId; hasAvatar?: boolean};

/**
 * Renders User initials in SVG
 */
interface UserWithName {
  firstName: string;
  lastName: string;
  color: string;
}

/**
 * Renders Anonymous user SVG
 */
type EmptyUser = undefined | {};

type PartialUser = UserWithId | UserWithName | EmptyUser;

interface UserAvatarProps {
  user: PartialUser;
}

function getAvatarId(user: PartialUser = {}): string | undefined {
  // hasAvatar is allowed to be undefined. But if it's false then don't hit the api
  if ('id' in user && !!user.id && user.hasAvatar !== false) {
    return user.id;
  }
  if ('userId' in user && !!user.userId && user.hasAvatar !== false) {
    return user.userId;
  }
}

function showInitials(user: PartialUser = {}): user is UserWithName {
  if ('firstName' in user && !!user.firstName && 'lastName' in user && !!user.lastName) {
    return true;
  }
  return false;
}

export const UserAvatar: React.FC<UserAvatarProps> = ({user = {}}) => {
  const [useFallback, setUseFallback] = useState(false);
  const avatar = useMemo(() => {
    const avatarId = getAvatarId(user);
    if (!useFallback && avatarId) {
      return (
        <img
          alt={getFullName(user)}
          src={`/api/assets/avatar/${avatarId}`}
          className="img-avatar"
          onError={() => setUseFallback(true)}
        />
      );
    }

    if (showInitials(user)) {
      const initials = (user.firstName[0] + user.lastName[0]).toUpperCase();
      return <CircleTextSVG content={initials} color={user.color} />;
    }

    return <CircledUserIcon className="default-avatar" />;
  }, [user, useFallback, setUseFallback]);

  return <div className="avatar new-user-avatar">{avatar}</div>;
};

interface CircleTextAvatarProps {
  content: string | number;
  color: string;
}

function CircleTextSVG({content, color}: CircleTextAvatarProps) {
  return (
    <svg viewBox="0 0 100 100" className="initials-avatar">
      <circle cx="50%" cy="50%" r={47} stroke={`#${color}`} strokeWidth="5" fill="#fff" />
      <Text
        x="50%"
        y="50%"
        fill={`#${color}`}
        textAnchor="middle"
        verticalAnchor="middle"
        fontSize="46px">
        {content}
      </Text>
    </svg>
  );
}

export function CircleTextAvatar({color, content}: CircleTextAvatarProps) {
  return (
    <div className="avatar new-user-avatar">
      <CircleTextSVG content={content} color={color} />
    </div>
  );
}
